import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import { useMemo } from 'react';
import { usePermissions } from 'utils/hooks';
import { TFilterConfig } from 'utils/types';

import { Box, Flex, Typography } from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

interface FilterSectionProps {
  title: string;
  text: string;
  Component: JSX.Element;
}

function FilterSection({ title, text, Component }: FilterSectionProps) {
  const { t } = useTranslation();
  return (
    <Box
      data-testid="filter-section"
      css={{ '&:not(&:last-of-type)': { marginBottom: '$large' } }}
    >
      <Flex
        data-testid={title}
        align="center"
        gap="x-small"
        css={{ marginBottom: '$medium' }}
      >
        <Typography variant="subtextBold">{t(title)}</Typography>
        <Typography variant="subtext" css={{ color: '$text-tertiary' }}>
          &#8211;
        </Typography>
        <Typography variant="subtext" css={{ color: '$text-tertiary' }}>
          {t(text)}
        </Typography>
      </Flex>
      {Component}
    </Box>
  );
}

interface IFilterPanelContent {
  filters: (flags: LDFlagSet) => TFilterConfig;
}

function FilterPanelContent({ filters }: IFilterPanelContent) {
  const { t } = useTranslation();
  const permissions = usePermissions();

  const flaggedFilters = useMemo(
    () =>
      filters(permissions).map(({ id, groupName, items }) => (
        <Box
          key={id}
          css={{
            borderBottom: '1px solid $border-primary',
            padding: '$medium',
          }}
        >
          <Typography
            variant="bodyBold"
            as="div"
            css={{ marginBottom: '$medium' }}
          >
            {t(groupName)}
          </Typography>
          {items.map((criteria) => (
            <FilterSection key={criteria.title} {...criteria} />
          ))}
        </Box>
      )),
    [permissions, filters, t],
  );

  return (
    <Flex data-testid="filter-panel" orientation="vertical">
      {flaggedFilters}
    </Flex>
  );
}

export default FilterPanelContent;
